import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Snackbar,
  Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import UploadButton from '../../components/UploadButton';
import { uploadZoneTypeFile } from '../../resources/uploadFiles';
import { InputChangeEvent } from '../../resources/utils';
import { Props } from './interface';
import Title from '../../components/Title';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import Select from '../../components/Select';
import { SelectOption } from '../../components/Select/interface';
import {
  clearZoneState,
  createZoneAsync,
  getZonesAsync,
  setZoneState,
  updateZoneAsync
} from '../../redux/slices/zonesSlice';
import { ZoneType } from '../../resources/types/zonesTypes';
import { selectAdmins } from '../../redux/slices/adminsSlice';

const ZoneModal = (props: Props) => {
  const { open, onClose } = props;
  const theme = useTheme();

  const dispatch = useDispatch();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!open) {
      dispatch(clearZoneState());
    }
  }, [dispatch, open]);

  const isSuperAdmin = useSelector(
    (state: RootState) => state.auth.info?.type === 'superAdmin'
  );
  const cities = useSelector((state: RootState) => state.cities.items);
  const zoneState = useSelector((state: RootState) => state.zones.zone);
  const adminInfo = useSelector(selectAdmins);

  const currentCityId = useMemo(() => {
    const city = cities.find(item => item._id === adminInfo.currentCity);
    if (city) {
      return city._id;
    }
  }, [adminInfo.currentCity, cities]);

  const typesOptions = useMemo<SelectOption[]>(
    () => [
      { value: 'blue', label: 'Синя' },
      { value: 'green', label: 'Зелена' },
      { value: 'yellow', label: 'Жовта' },
      { value: 'red', label: 'Червона' },
      { value: 'purple', label: 'Фіолетова' },
      { value: 'brown', label: 'Коричнева' }
    ],
    []
  );

  const citiesOptions = useMemo<SelectOption[]>(
    () =>
      cities.map(city => ({
        value: city._id,
        label: city.cityName
      })),
    [cities]
  );

  const [isUploadingFile, setIsUploadingFile] = useState(false);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Title>{zoneState._id ? 'Редагування зони' : 'Створення зони'}</Title>
      </DialogTitle>
      <DialogContent>
        <Grid container direction='column' spacing={2}>
          {isSuperAdmin && (
            <Grid item>
              <Select
                fullWidth
                options={citiesOptions}
                onChange={value => dispatch(setZoneState({ cityId: value }))}
                value={zoneState.cityId}
                label='Місто'
              />
            </Grid>
          )}
          {zoneState._id && zoneState.type === 'blue' ? (
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={zoneState.prohibitStop ?? false}
                    color='primary'
                    onChange={(event, checked) =>
                      dispatch(setZoneState({ prohibitStop: checked }))
                    }
                  />
                }
                label='Заборонити паркування'
              />
            </Grid>
          ) : null}
          <Grid item>
            <UploadButton
              loading={isUploadingFile}
              buttonProps={{
                color: 'primary',
                variant: 'text'
              }}
              onChange={async (e: InputChangeEvent) => {
                const { files } = e.target;

                if (files) {
                  const file = files[0];

                  setIsUploadingFile(true);

                  uploadZoneTypeFile(
                    zoneState.type,
                    zoneState.cityId || currentCityId,
                    file,
                    async (changed: any) => {
                      console.log('-=-=- changed', changed);
                      setIsUploadingFile(false);
                      setSnackbar({
                        open: true,
                        message: 'Зони успішно оновлено',
                        severity: 'success'
                      });
                    },
                    () => {
                      setIsUploadingFile(false);
                      setSnackbar({
                        open: true,
                        message: 'Помилка при завантаженні зон',
                        severity: 'error'
                      });
                    }
                  );
                }
              }}
            >
              Завантажити нові координати
            </UploadButton>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          onClick={async () => {
            dispatch(getZonesAsync(true));
            if (onClose) {
              onClose({}, 'escapeKeyDown');
            }
          }}
          sx={{
            bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
            color: theme.palette.mode === 'light' ? '#000' : '#FFF',
            '&:hover': {
              bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A'
            }
          }}
        >
          Закрити
        </Button>
      </DialogActions>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ZoneModal;
